import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Category, Product } from '@equinox/shared';
import { formatCents } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { categoriesApi, productsApi, productStockApi } from '../../api/resources.js';
import type { UpdateProductRequest } from '../../api/types.js';
import { ApiError } from '../../lib/apiClient.js';
import { MoneyInput } from '../../components/MoneyInput.js';
import { effectiveStock } from './productStock.js';
import { CellEditPopover } from '../../components/CellEditPopover.js';
import { TilePreview } from '../../components/TilePreview.js';
import { Banner } from '../../components/Banner.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { SortableColumnHeader } from '../../components/table/SortableColumnHeader.js';
import { FilterSummary, RangeFilterInput, SelectFilterInput, TextFilterInput } from '../../components/table/FilterInputs.js';
import { useTableSort } from '../../lib/dataTable/useTableSort.js';
import { useColumnFilters } from '../../lib/dataTable/useColumnFilters.js';
import { moneyRangeColumn, numberRangeColumn, selectColumn, textContainsColumn } from '../../lib/dataTable/filters.js';
import { ProductFormModal } from './ProductFormModal.js';
import { ImportModal } from './ImportModal.js';
import { StockInput } from './StockInput.js';
import { EditableProductRow } from './EditableProductRow.js';
import { draftFromProduct, isDraftDirty, rowValidationProblem, type ProductDraft } from './productDraft.js';

const LOW_STOCK_THRESHOLD = 5;

/**
 * The products table's column geometry, declared once and applied through a
 * `<colgroup>` with `table-layout: fixed` (see `.table--stable` in app.css).
 *
 * These widths are the fix for half of OneDev xyntara/equinox #1 and #2: with
 * automatic table layout every column was sized from whatever happened to be
 * inside it, so replacing a price with an input, or a category name with a
 * `<select>`, re-measured and re-positioned *every* column and knocked the
 * headers out of alignment with their own data. Declared widths cannot be
 * argued with by cell content.
 *
 * Order and count must match the header row exactly. Adding a column here
 * without adding it there (or vice versa) silently mis-aligns the whole
 * table, so `ProductsPage.test.tsx` pins the two together.
 */
const COLUMN_WIDTHS = [
  48, // selection checkbox
  72, // image thumbnail (40px tile + padding)
  0, // name — takes whatever is left over
  150, // SKU
  170, // category
  130, // price
  130, // cost — a money column, sized like its sibling money column
  110, // stock
  100, // sort order
  120, // status
  290, // actions / row status — Editar + Duplicar + Desactivar/Reactivar, measured
       // in a real browser with a margin: 3 buttons alone render ~275px wide
       // (font-dependent) in a `white-space: nowrap` cell, so 270 was already
       // too tight (found in integrator review, headless Chromium). Bumping
       // this requires bumping `.table--stable`'s `min-width` by the same
       // delta — see app.css's comment on that rule.
] as const;

/** Zero and negative are flagged strongly; a low-but-positive count is a subtler nudge. Never a shout — see the house copy rules. */
function optionsStockColor(product: Product): string | undefined {
  return stockColor(effectiveStock(product));
}

function stockColor(qty: number): string | undefined {
  if (qty <= 0) return 'var(--danger-text)';
  if (qty <= LOW_STOCK_THRESHOLD) return 'var(--warn-text)';
  return undefined;
}

export function ProductsPage() {
  const { t } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  /**
   * Single-cell edit state. The trigger element is held alongside the product
   * because the editor is an overlay anchored to that exact button rather than
   * markup swapped into the cell — see `CellEditPopover` and OneDev
   * xyntara/equinox #2. The row itself never re-renders while editing, which
   * is what makes "zero displacement" true by construction instead of by
   * careful CSS.
   */
  const [priceEdit, setPriceEdit] = useState<{ product: Product; anchor: HTMLElement } | null>(null);
  const [pendingCents, setPendingCents] = useState<number | null>(null);
  const [pendingPriceValid, setPendingPriceValid] = useState(true);
  const [stockEdit, setStockEdit] = useState<{ product: Product; anchor: HTMLElement } | null>(null);
  const [pendingStockQty, setPendingStockQty] = useState<number | null>(null);
  const [pendingStockValid, setPendingStockValid] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<Product | 'new' | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Product | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<Product | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);

  // ---- Table edit mode ("Edición en tabla") ----
  //
  // A whole-catalog inline edit alternative to the one-modal-at-a-time flow,
  // see docs/decisions/2026-07-31-products-table-edit-mode.md. `drafts` only
  // ever holds an entry for a row the admin has actually touched — an
  // untouched row is never dirty and never allocates one. Keyed by product
  // id rather than by visible row index so a draft survives the row
  // scrolling in and out of the current sort/filter view.
  const [tableEditMode, setTableEditMode] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, ProductDraft>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [tableEditSaving, setTableEditSaving] = useState(false);
  const [tableEditSaved, setTableEditSaved] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [p, c] = await Promise.all([productsApi.list(), categoriesApi.list()]);
      setProducts(p);
      setCategories(c);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const searchFiltered = products.filter((p) => {
    if (categoryFilter && p.categoryId !== categoryFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
  });

  // Per-column filter row (name/SKU contains, price/stock range, active
  // select) layers on top of the existing search box and category select
  // rather than duplicating them — see
  // docs/decisions/2026-07-31-admin-table-sort-filter.md.
  const columnFilters = useColumnFilters(searchFiltered, {
    name: textContainsColumn<Product>((p) => p.name),
    sku: textContainsColumn<Product>((p) => p.sku),
    priceCents: moneyRangeColumn<Product>((p) => p.priceCents),
    costCents: moneyRangeColumn<Product>((p) => p.costCents ?? null),
    stockQty: numberRangeColumn<Product>((p) => effectiveStock(p)),
    active: selectColumn<Product>((p) => String(p.active)),
  });
  const visibleRows = columnFilters.rows;

  const sort = useTableSort(visibleRows, {
    name: { type: 'string', value: (p: Product) => p.name },
    sku: { type: 'string', value: (p: Product) => p.sku },
    category: {
      type: 'string',
      value: (p: Product) => (p.categoryId ? (categoryById.get(p.categoryId)?.name ?? '') : ''),
    },
    priceCents: { type: 'number', value: (p: Product) => p.priceCents },
    costCents: { type: 'number', value: (p: Product) => p.costCents ?? null },
    stockQty: { type: 'number', value: (p: Product) => effectiveStock(p) },
    sortOrder: { type: 'number', value: (p: Product) => p.sortOrder },
    active: { type: 'boolean', value: (p: Product) => p.active },
  });
  const filtered = sort.rows;

  // Selection is meaningful only within the current filter: a row scrolled
  // out of view by a search, category, or column filter change should not
  // stay silently selected for a batch action the admin can no longer see.
  useEffect(() => {
    const visibleIds = new Set(visibleRows.map((p) => p.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the visible id set actually changes
  }, [search, categoryFilter, products, columnFilters.values]);

  const allFilteredSelected = visibleRows.length > 0 && visibleRows.every((p) => selectedIds.has(p.id));

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (allFilteredSelected) return new Set();
      return new Set(visibleRows.map((p) => p.id));
    });
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function commitPrice(product: Product) {
    if (pendingCents == null || !pendingPriceValid || pendingCents === product.priceCents) {
      setPriceEdit(null);
      return;
    }
    setSavingId(product.id);
    try {
      const updated = await productsApi.setPrice(product.id, pendingCents);
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setPriceEdit(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSavingId(null);
    }
  }

  async function commitStock(product: Product) {
    if (pendingStockQty == null || !pendingStockValid || pendingStockQty === product.stockQty) {
      setStockEdit(null);
      return;
    }
    setSavingId(product.id);
    try {
      const updated = await productStockApi.adjust(product.id, pendingStockQty);
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setStockEdit(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSavingId(null);
    }
  }

  /** Enter saves, Escape cancels — the popover's own ESC handler covers the latter. */
  function onEditorKeyDown(event: ReactKeyboardEvent, commit: () => void) {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  }

  /**
   * Sequential, not `Promise.all` — a partial failure must produce a precise
   * per-row report, never a silent partial success mixed in with a generic
   * "something failed".
   */
  async function runBatch(action: 'activate' | 'deactivate') {
    const targets = filtered.filter((p) => selectedIds.has(p.id));
    if (targets.length === 0) return;
    setBatchBusy(true);
    setBatchError(null);
    const failures: string[] = [];
    for (const product of targets) {
      try {
        const updated = action === 'activate' ? await productsApi.activate(product.id) : await productsApi.deactivate(product.id);
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      } catch (err) {
        failures.push(`${product.sku} — ${err instanceof ApiError ? err.message : t('common.error')}`);
      }
    }
    setBatchBusy(false);
    setSelectedIds(new Set());
    if (failures.length > 0) {
      setBatchError(t('products.batch.someFailed', { count: failures.length }) + ': ' + failures.join('; '));
    }
  }

  // ---- Table edit mode ----

  function draftFor(product: Product): ProductDraft {
    return drafts[product.id] ?? draftFromProduct(product);
  }

  function updateDraft(product: Product, patch: Partial<ProductDraft>) {
    setDrafts((prev) => ({ ...prev, [product.id]: { ...draftFor(product), ...patch } }));
    setTableEditSaved(false);
  }

  const dirtyIds = useMemo(
    () =>
      Object.keys(drafts).filter((id) => {
        const product = products.find((p) => p.id === id);
        return product ? isDraftDirty(product, drafts[id]!) : false;
      }),
    [drafts, products],
  );

  function enterTableEdit() {
    setTableEditMode(true);
    // Batch selection is hidden while editing (see decision doc) — clearing
    // it here rather than leaving it to silently reappear on exit.
    setSelectedIds(new Set());
    // A single-cell editor is anchored to a button that this mode is about to
    // unmount; leaving it open would strand an overlay over a row it no longer
    // belongs to.
    setPriceEdit(null);
    setStockEdit(null);
  }

  function exitTableEdit() {
    setTableEditMode(false);
    setDrafts({});
    setRowErrors({});
    setCancelConfirmOpen(false);
    setTableEditSaved(false);
  }

  function requestExitTableEdit() {
    if (dirtyIds.length > 0) {
      setCancelConfirmOpen(true);
    } else {
      exitTableEdit();
    }
  }

  function messageForProblem(problem: 'nameOrSkuRequired' | 'invalidPrice' | 'invalidStock'): string {
    if (problem === 'nameOrSkuRequired') return t('products.tableEdit.nameSkuRequired');
    if (problem === 'invalidPrice') return t('products.priceInvalid');
    return t('products.stockInvalid');
  }

  /**
   * Persists every dirty row, one at a time — never `Promise.all`, same
   * reasoning as `runBatch` above: a partial failure must report exactly
   * which rows failed and why, and a row that fails must stay dirty (with
   * its message) rather than silently rolling back or disappearing.
   * Successful rows clear immediately, independent of any row that follows.
   */
  async function saveTableEdits() {
    if (dirtyIds.length === 0) return;
    setTableEditSaving(true);
    setTableEditSaved(false);
    const attemptedIds = [...dirtyIds];
    const nextRowErrors: Record<string, string> = {};
    let failureCount = 0;

    for (const id of attemptedIds) {
      const product = products.find((p) => p.id === id);
      if (!product) continue;
      const draft = drafts[id]!;
      const problem = rowValidationProblem(draft);
      if (problem) {
        nextRowErrors[id] = messageForProblem(problem);
        failureCount++;
        continue;
      }
      try {
        let updated = product;
        const patch: UpdateProductRequest = {};
        let patchNeeded = false;
        const trimmedName = draft.name.trim();
        const trimmedSku = draft.sku.trim();
        const draftCategoryId = draft.categoryId || null;
        const draftImageUrl = draft.imageUrl.trim() || null;
        if (trimmedName !== product.name) {
          patch.name = trimmedName;
          patchNeeded = true;
        }
        if (trimmedSku !== product.sku) {
          patch.sku = trimmedSku;
          patchNeeded = true;
        }
        if (draftCategoryId !== product.categoryId) {
          patch.categoryId = draftCategoryId;
          patchNeeded = true;
        }
        if (draft.priceCents != null && draft.priceCents !== product.priceCents) {
          patch.priceCents = draft.priceCents;
          patchNeeded = true;
        }
        if (draft.costCents !== (product.costCents ?? null)) {
          patch.costCents = draft.costCents;
          patchNeeded = true;
        }
        if (draftImageUrl !== product.imageUrl) {
          patch.imageUrl = draftImageUrl;
          patchNeeded = true;
        }
        if (draft.sortOrder !== product.sortOrder) {
          patch.sortOrder = draft.sortOrder;
          patchNeeded = true;
        }
        if (draft.active !== product.active) {
          patch.active = draft.active;
          patchNeeded = true;
        }
        if (patchNeeded) {
          updated = await productsApi.update(id, patch);
        }
        // Stock never travels through the generic PATCH above — same rule
        // as the create/edit modal (see routes/products.ts): a dedicated
        // call, and only when the value actually changed, so a row with
        // every other field dirty but stock untouched never writes a no-op
        // movement.
        if (draft.stockQty != null && draft.stockQty !== product.stockQty) {
          updated = await productStockApi.adjust(id, draft.stockQty);
        }
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } catch (err) {
        nextRowErrors[id] = err instanceof ApiError ? err.message : t('common.error');
        failureCount++;
      }
    }

    setRowErrors((prev) => {
      const next = { ...prev };
      for (const id of attemptedIds) delete next[id];
      return { ...next, ...nextRowErrors };
    });
    setTableEditSaving(false);
    setTableEditSaved(failureCount === 0);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('products.title')}</h1>
          <p>{t('products.subtitle')}</p>
        </div>
        <div className="toolbar">
          {tableEditMode ? (
            <>
              {dirtyIds.length > 0 && (
                <span className="field-hint">{t('products.tableEdit.pendingCount', { count: dirtyIds.length })}</span>
              )}
              <button type="button" className="btn btn--secondary" onClick={requestExitTableEdit} disabled={tableEditSaving}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={dirtyIds.length === 0 || tableEditSaving}
                onClick={() => void saveTableEdits()}
              >
                {tableEditSaving ? t('products.tableEdit.saving') : t('products.tableEdit.save')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn--secondary" onClick={enterTableEdit}>
                {t('products.tableEdit.toggle')}
              </button>
              <button type="button" className="btn btn--secondary" onClick={() => setShowImport(true)}>
                {t('products.import.action')}
              </button>
              <button type="button" className="btn btn--primary" onClick={() => setFormTarget('new')}>
                {t('products.new')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}
      {batchError && <Banner kind="danger">{batchError}</Banner>}
      {tableEditSaved && <Banner kind="ok">{t('common.saved')}</Banner>}
      {/*
       * Every failing row's full message, by SKU. The row's own status cell
       * shows a badge (a sentence there would wrap and reflow the table — see
       * `EditableProductRow`), so this banner is where the untruncated text
       * lives. Same shape as the batch activate/deactivate failure report.
       */}
      {Object.keys(rowErrors).length > 0 && (
        <Banner kind="danger">
          {t('products.tableEdit.saveFailed', { count: Object.keys(rowErrors).length })}:{' '}
          {Object.entries(rowErrors)
            .map(([id, message]) => `${products.find((p) => p.id === id)?.sku ?? id} — ${message}`)
            .join('; ')}
        </Banner>
      )}

      <div className="toolbar">
        <input
          type="search"
          placeholder={t('products.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 240 }}
          aria-label={t('products.search')}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label={t('products.category')}
        >
          <option value="">{t('products.allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <FilterSummary activeCount={columnFilters.activeCount} onClear={columnFilters.clear} />

      {/*
       * Batch select/activate/deactivate is hidden in table edit mode rather
       * than left to compose with it: `active` is already one of the fields
       * every row edits directly in this mode, and a second, overlapping
       * "which rows are selected for what" control would read as ambiguous
       * chrome right when the admin's attention is already split across a
       * whole table of live inputs. Selection state itself is cleared on
       * entering the mode (see `enterTableEdit`) rather than merely hidden,
       * so it can't reappear stale on exit. See
       * docs/decisions/2026-07-31-products-table-edit-mode.md.
       */}
      {!tableEditMode && selectedIds.size > 0 && (
        <div className="toolbar" role="toolbar" aria-label={t('products.batch.toolbarLabel')}>
          <span>{t('products.batch.selectedCount', { count: selectedIds.size })}</span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={batchBusy}
            onClick={() => void runBatch('activate')}
          >
            {batchBusy ? t('common.loading') : t('products.batch.activate')}
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={batchBusy}
            onClick={() => void runBatch('deactivate')}
          >
            {batchBusy ? t('common.loading') : t('products.batch.deactivate')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelectedIds(new Set())}>
            {t('products.batch.clearSelection')}
          </button>
        </div>
      )}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : filtered.length === 0 ? (
        <p>{searchFiltered.length === 0 ? t('products.noResults') : t('table.noMatches')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table--stable">
            {/*
             * Declared column geometry — see COLUMN_WIDTHS. `width: 0` means
             * "no declared width", which under `table-layout: fixed` hands the
             * column whatever the others leave over.
             */}
            <colgroup>
              {COLUMN_WIDTHS.map((width, i) => (
                <col key={i} style={width > 0 ? { width } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>
                  {!tableEditMode && (
                    <input
                      type="checkbox"
                      aria-label={t('products.batch.selectAll')}
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                    />
                  )}
                </th>
                <th>{t('products.image')}</th>
                <SortableColumnHeader label={t('products.name')} ariaSort={sort.ariaSortFor('name')} onSort={() => sort.toggleSort('name')} />
                <SortableColumnHeader label={t('products.sku')} ariaSort={sort.ariaSortFor('sku')} onSort={() => sort.toggleSort('sku')} />
                <SortableColumnHeader label={t('products.category')} ariaSort={sort.ariaSortFor('category')} onSort={() => sort.toggleSort('category')} />
                <SortableColumnHeader
                  label={t('products.price')}
                  align="right"
                  ariaSort={sort.ariaSortFor('priceCents')}
                  onSort={() => sort.toggleSort('priceCents')}
                />
                <SortableColumnHeader
                  label={t('products.cost')}
                  align="right"
                  ariaSort={sort.ariaSortFor('costCents')}
                  onSort={() => sort.toggleSort('costCents')}
                />
                <SortableColumnHeader
                  label={t('products.stock')}
                  align="right"
                  ariaSort={sort.ariaSortFor('stockQty')}
                  onSort={() => sort.toggleSort('stockQty')}
                />
                <SortableColumnHeader
                  label={t('products.sortOrder')}
                  align="right"
                  ariaSort={sort.ariaSortFor('sortOrder')}
                  onSort={() => sort.toggleSort('sortOrder')}
                />
                <SortableColumnHeader label={t('common.status')} ariaSort={sort.ariaSortFor('active')} onSort={() => sort.toggleSort('active')} />
                <th>{tableEditMode ? t('products.tableEdit.rowStatus') : t('common.actions')}</th>
              </tr>
              <tr className="table-filter-row">
                <th />
                <th />
                <th>
                  <TextFilterInput
                    id="products-filter-name"
                    column={t('products.name')}
                    value={columnFilters.values.name as string}
                    onChange={(value) => columnFilters.setValue('name', value)}
                  />
                </th>
                <th>
                  <TextFilterInput
                    id="products-filter-sku"
                    column={t('products.sku')}
                    value={columnFilters.values.sku as string}
                    onChange={(value) => columnFilters.setValue('sku', value)}
                  />
                </th>
                {/* Category already has its own filter above the table — not duplicated here. */}
                <th />
                <th className="numeric">
                  <RangeFilterInput
                    id="products-filter-price"
                    column={t('products.price')}
                    value={columnFilters.values.priceCents as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('priceCents', value)}
                    affix={{ symbol: '$', position: 'prefix' }}
                    placeholder="0.00"
                  />
                </th>
                <th className="numeric">
                  <RangeFilterInput
                    id="products-filter-cost"
                    column={t('products.cost')}
                    value={columnFilters.values.costCents as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('costCents', value)}
                    affix={{ symbol: '$', position: 'prefix' }}
                    placeholder="0.00"
                  />
                </th>
                <th className="numeric">
                  <RangeFilterInput
                    id="products-filter-stock"
                    column={t('products.stock')}
                    value={columnFilters.values.stockQty as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('stockQty', value)}
                    placeholder="0"
                  />
                </th>
                {/* Sort order has no dedicated filter — sorting the column already covers the one thing an admin needs to do with it. */}
                <th />
                <th>
                  <SelectFilterInput
                    id="products-filter-active"
                    column={t('common.status')}
                    value={columnFilters.values.active as string}
                    onChange={(value) => columnFilters.setValue('active', value)}
                    options={[
                      { value: 'true', label: t('common.active') },
                      { value: 'false', label: t('common.inactive') },
                    ]}
                  />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) =>
                tableEditMode ? (
                  <EditableProductRow
                    key={product.id}
                    product={product}
                    categories={categories}
                    draft={draftFor(product)}
                    dirty={dirtyIds.includes(product.id)}
                    error={rowErrors[product.id]}
                    disabled={tableEditSaving}
                    onChange={(patch) => updateDraft(product, patch)}
                    t={t}
                  />
                ) : (
                  (() => {
                    const category = product.categoryId ? categoryById.get(product.categoryId) : undefined;
                    const color = category?.color ?? 'var(--accent)';
                    const stockTextColor = stockColor(product.stockQty);
                    return (
                      <tr key={product.id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={t('products.batch.selectRow', { name: product.name })}
                            checked={selectedIds.has(product.id)}
                            onChange={() => toggleSelect(product.id)}
                          />
                        </td>
                        <td>
                          <TilePreview label={product.name} color={color} imageUrl={product.imageUrl} size={40} />
                        </td>
                        <td title={product.name}>{product.name}</td>
                        <td className="numeric">{product.sku}</td>
                        <td title={category?.name}>{category?.name ?? '—'}</td>
                        {/*
                         * The trigger is the whole cell content in both states.
                         * Clicking it opens an anchored overlay (rendered once,
                         * below the table) rather than swapping markup in here,
                         * so the cell, the row and every column keep exactly the
                         * geometry they had. `aria-expanded` announces the state
                         * without occupying a pixel.
                         */}
                        <td className="numeric">
                          {(product.options?.length ?? 0) > 0 ? (
                            // A product sold by options is priced by them (its own
                            // price is the cheapest active option): edited in the
                            // product form, never as a lone number here. The figure
                            // alone with a «+» (from), so the whole value always fits
                            // the declared column; «Desde» is hidden text for readers.
                            <span
                              title={t('products.options.priceDerived')}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              <span className="sr-only">{t('products.fromPriceLead')} </span>
                              {t('products.fromPriceCell', { price: formatCents(product.priceCents) })}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              aria-haspopup="dialog"
                              aria-expanded={priceEdit?.product.id === product.id}
                              onClick={(e) => {
                                setPriceEdit({ product, anchor: e.currentTarget });
                                setPendingCents(product.priceCents);
                                setPendingPriceValid(true);
                              }}
                            >
                              {formatCents(product.priceCents)}
                            </button>
                          )}
                        </td>
                        <td className="numeric">
                          {/* Cost is read-only here — main added no click-to-edit for it. */}
                          {product.costCents != null ? formatCents(product.costCents) : '—'}
                        </td>
                        <td className="numeric">
                          {(product.options?.length ?? 0) > 0 ? (
                            // Stock of a product sold by options lives on its options;
                            // the total is shown, read-only (the form edits each one).
                            <span
                              className="numeric"
                              title={t('products.options.stockOnOptions')}
                              style={{
                                whiteSpace: 'nowrap',
                                ...(optionsStockColor(product) ? { color: optionsStockColor(product) } : {}),
                              }}
                            >
                              {effectiveStock(product)}
                            </span>
                          ) : (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm numeric"
                            style={stockTextColor ? { color: stockTextColor } : undefined}
                            aria-haspopup="dialog"
                            aria-expanded={stockEdit?.product.id === product.id}
                            onClick={(e) => {
                              setStockEdit({ product, anchor: e.currentTarget });
                              setPendingStockQty(product.stockQty);
                              setPendingStockValid(true);
                            }}
                          >
                            {product.stockQty}
                          </button>
                          )}
                        </td>
                        <td className="numeric">{product.sortOrder}</td>
                        <td>
                          <span className={`badge badge--${product.active ? 'ok' : 'neutral'}`}>
                            {product.active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                        <td className="cell-actions">
                          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setFormTarget(product)}>
                            {t('common.edit')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setDuplicateTarget(product)}
                          >
                            {t('products.duplicate')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setDeactivateTarget(product)}
                          >
                            {product.active ? t('products.deactivate') : t('products.activate')}
                          </button>
                        </td>
                      </tr>
                    );
                  })()
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {/*
       * Single-cell editors. Rendered here, outside the table, and portalled
       * to `document.body` by `CellEditPopover` — they float above the page
       * instead of growing the cell they belong to. This is the shape
       * ROLES-DESIGNER.md prescribes when an edit state cannot fit its rest
       * state's footprint, and the fix for OneDev xyntara/equinox #2.
       */}
      {priceEdit && (
        <CellEditPopover
          anchor={priceEdit.anchor}
          title={t('products.tableEdit.fieldLabel', { field: t('products.price'), name: priceEdit.product.name })}
          onCancel={() => setPriceEdit(null)}
        >
          <div onKeyDown={(e) => onEditorKeyDown(e, () => void commitPrice(priceEdit.product))}>
            <MoneyInput
              label={t('products.price')}
              ariaLabel={t('products.price')}
              valueCents={priceEdit.product.priceCents}
              onChange={(cents, valid) => {
                setPendingCents(cents);
                setPendingPriceValid(valid);
              }}
              autoFocus
            />
          </div>
          <div className="cell-edit__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPriceEdit(null)}>
              {t('products.cancelEdit')}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={savingId === priceEdit.product.id || !pendingPriceValid}
              onClick={() => void commitPrice(priceEdit.product)}
            >
              {savingId === priceEdit.product.id ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </CellEditPopover>
      )}

      {stockEdit && (
        <CellEditPopover
          anchor={stockEdit.anchor}
          title={t('products.tableEdit.fieldLabel', { field: t('products.stock'), name: stockEdit.product.name })}
          onCancel={() => setStockEdit(null)}
        >
          <div onKeyDown={(e) => onEditorKeyDown(e, () => void commitStock(stockEdit.product))}>
            <StockInput
              label={t('products.stock')}
              ariaLabel={t('products.stock')}
              valueQty={stockEdit.product.stockQty}
              onChange={(qty, valid) => {
                setPendingStockQty(qty);
                setPendingStockValid(valid);
              }}
              autoFocus
            />
          </div>
          <div className="cell-edit__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setStockEdit(null)}>
              {t('products.cancelEdit')}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={savingId === stockEdit.product.id || !pendingStockValid}
              onClick={() => void commitStock(stockEdit.product)}
            >
              {savingId === stockEdit.product.id ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </CellEditPopover>
      )}

      {(formTarget || duplicateTarget) && (
        <ProductFormModal
          product={formTarget && formTarget !== 'new' ? formTarget : null}
          duplicateFrom={duplicateTarget}
          categories={categories}
          onClose={() => {
            setFormTarget(null);
            setDuplicateTarget(null);
          }}
          onSaved={(saved) => {
            setProducts((prev) => {
              const exists = prev.some((p) => p.id === saved.id);
              return exists ? prev.map((p) => (p.id === saved.id ? saved : p)) : [saved, ...prev];
            });
            setFormTarget(null);
            setDuplicateTarget(null);
          }}
        />
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            setShowImport(false);
            void reload();
          }}
        />
      )}

      {deactivateTarget && (
        <ConfirmDialog
          title={deactivateTarget.active ? t('products.deactivate') : t('products.activate')}
          consequence={
            deactivateTarget.active ? t('products.deactivateConfirm') : t('products.deactivateConfirm')
          }
          danger={deactivateTarget.active}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setDeactivateTarget(null);
            try {
              const updated = target.active
                ? await productsApi.deactivate(target.id)
                : await productsApi.activate(target.id);
              setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
            }
          }}
        />
      )}

      {/*
       * ESC does not close/discard table edit mode — there is no keydown
       * handler wired to it at all, deliberately. The only way out with
       * unsaved edits is this explicit Cancel confirm (same rule the modal
       * ESC/busy-guard convention already follows for a blocking operation —
       * see docs/decisions/2026-07-31-modal-esc-and-busy-guard.md — applied
       * here to a page-level mode instead of a modal).
       */}
      {cancelConfirmOpen && (
        <ConfirmDialog
          title={t('products.tableEdit.cancelTitle')}
          consequence={t('products.tableEdit.cancelConfirm')}
          confirmLabel={t('products.tableEdit.discardConfirm')}
          danger
          onCancel={() => setCancelConfirmOpen(false)}
          onConfirm={exitTableEdit}
        />
      )}
    </div>
  );
}
